// The signed-in user's saved searches, shared by the catalog's "save search"
// button and the account area's list so both show the same state without
// loading it twice. The server decides what a match is (savedSearchModel.ts);
// sameCriteria below only mirrors its duplicate check.
import { ref } from 'vue'
import { userService } from '@/services/userService'
import type { SavedSearch, SavedSearchCriteria } from '@/types'

const list = ref<SavedSearch[] | null>(null)
const loadError = ref(false)
let inflight: Promise<void> | null = null

function norm(c: SavedSearchCriteria) {
  const slug = (v?: string | null) => (v && v !== 'all' ? v : null)
  const price = (v?: number | null) => (typeof v === 'number' && v > 0 ? v : null)
  return {
    query: (c.query || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('tr'),
    category: slug(c.category),
    subCategory: slug(c.subCategory),
    regionId: slug(c.regionId),
    minPrice: price(c.minPrice),
    maxPrice: price(c.maxPrice),
    blitz: !!c.blitz,
  }
}

// The filter a guest asked to save before being sent to sign in; the
// catalog finishes the save on return only if this is set (sessionStorage).
export const PENDING_SAVE_KEY = 'itorgo_pending_save_search'

export function sameCriteria(a: SavedSearchCriteria, b: SavedSearchCriteria): boolean {
  const x = norm(a)
  const y = norm(b)
  return x.query === y.query && x.category === y.category && x.subCategory === y.subCategory
    && x.regionId === y.regionId && x.minPrice === y.minPrice && x.maxPrice === y.maxPrice && x.blitz === y.blitz
}

// Worth saving: at least one condition that actually narrows the catalog
// (the server rejects the rest) and a price range that can match something.
export function isSaveable(c: SavedSearchCriteria): boolean {
  const n = norm(c)
  if (n.minPrice !== null && n.maxPrice !== null && n.minPrice > n.maxPrice) return false
  return n.query.length >= 2 || !!n.category || !!n.subCategory || !!n.regionId
    || n.minPrice !== null || n.maxPrice !== null || n.blitz
}

// The catalog address that shows this search (LiveAuctionsPage query keys).
export function catalogLocation(s: SavedSearchCriteria) {
  const n = norm(s)
  const query: Record<string, string> = {}
  if (s.query?.trim()) query.search = s.query.trim()
  if (n.category) query.category = n.category
  if (n.subCategory) query.subCategory = n.subCategory
  if (n.regionId) query.region = n.regionId
  if (n.minPrice !== null) query.minPrice = String(n.minPrice)
  if (n.maxPrice !== null) query.maxPrice = String(n.maxPrice)
  if (n.blitz) query.blitz = '1'
  return { path: '/auctions', query }
}

export function useSavedSearches() {
  async function load(force = false): Promise<void> {
    if (list.value && !force) return
    if (inflight) return inflight
    inflight = (async () => {
      try {
        const res = await userService.getSavedSearches()
        list.value = res?.success ? res.data : []
        loadError.value = !res?.success
      } catch {
        loadError.value = true
      } finally {
        inflight = null
      }
    })()
    return inflight
  }

  function find(c: SavedSearchCriteria): SavedSearch | undefined {
    return list.value?.find(s => sameCriteria(s, c))
  }

  async function save(c: SavedSearchCriteria): Promise<SavedSearch> {
    const res = await userService.saveSearch({
      query: c.query?.trim() || '',
      category: c.category && c.category !== 'all' ? c.category : null,
      subCategory: c.subCategory || null,
      regionId: c.regionId && c.regionId !== 'all' ? c.regionId : null,
      minPrice: c.minPrice || null,
      maxPrice: c.maxPrice || null,
      blitz: !!c.blitz,
    })
    const saved = res.data
    const rest = (list.value || []).filter(s => s.id !== saved.id)
    list.value = [saved, ...rest]
    return saved
  }

  async function remove(id: string): Promise<void> {
    await userService.deleteSavedSearch(id)
    list.value = (list.value || []).filter(s => s.id !== id)
  }

  async function setEmail(id: string, on: boolean): Promise<void> {
    const res = await userService.setSavedSearchEmail(id, on)
    list.value = (list.value || []).map(s => (s.id === id ? res.data : s))
  }

  // Signing out must not leave the previous user's searches on screen.
  function reset() {
    list.value = null
    loadError.value = false
  }

  return { list, loadError, load, find, save, remove, setEmail, reset }
}
